import React from "react";
import { Link } from "react-router-dom";
import { Star, ChevronRight } from "lucide-react";

/**
 * Compact card surfacing a private-park rental within a city or
 * category page. Visually distinct from `ParkCard` so users immediately
 * understand it isn't a public park — green "Private Park" pill on the
 * thumbnail, "from $X/hr" pricing, founding-host badge.
 *
 * Click anywhere on the card → navigate to /private-parks/<slug>.
 */
const PrivateParkCard = ({ park }) => {
  if (!park) return null;
  const hero = park.photos && park.photos.length ? park.photos[0] : null;
  return (
    <Link
      to={`/private-parks/${park.slug}`}
      data-testid={`private-park-card-${park.slug}`}
      className="group relative flex gap-4 p-3 rounded-2xl bg-white border border-brand-border-warm hover:border-brand-forest hover:shadow-warm transition-all"
    >
      <div className="relative w-28 sm:w-36 aspect-square shrink-0 rounded-xl overflow-hidden bg-brand-cream-deep">
        {hero ? (
          <img
            src={hero}
            alt={park.name}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : null}
        <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand-forest text-white text-[10px] font-semibold tracking-wide">
          🏡 Private
        </span>
      </div>

      <div className="flex-1 min-w-0 py-1">
        <div className="flex items-start justify-between gap-2">
          <div className="font-heading font-bold text-brand-ink leading-tight line-clamp-2">
            {park.name}
          </div>
          <ChevronRight className="w-4 h-4 text-brand-ink/40 group-hover:text-brand-forest mt-0.5 shrink-0" />
        </div>
        {park.foundingHost && (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#fef3c7] text-[#b45309] text-[10px] font-semibold mt-1">
            ⭐ Founding Host
          </span>
        )}
        <div className="text-xs text-brand-ink/60 mt-1 line-clamp-1">{park.address}</div>
        <div className="flex items-center gap-2 mt-2 text-xs">
          <span className="inline-flex items-center gap-1 text-[#d4a017] font-semibold">
            <Star className="w-3 h-3" fill="currentColor" stroke="currentColor" />
            <span className="text-brand-ink">{park.rating.toFixed(1)}</span>
            <span className="text-brand-ink/50 font-normal">({park.reviewCount})</span>
          </span>
          <span className="text-brand-ink/30">·</span>
          <span className="text-brand-ink font-semibold">
            from ${park.pricing.firstDogPerHour}/hr
          </span>
        </div>
      </div>
    </Link>
  );
};

export default PrivateParkCard;
